'use client'

// TODO: Fix mobile spaceBetween image and content
// TODO: Improve autoplay progress animation

import useMedia from '@/hooks/use-media/use-media'
import { cn } from '@/lib/utils'
import { Base, ButtonIcon, Container, Div } from '@/ui/components'
import { MEDIA } from '@spot'
import { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel'
import Autoplay from 'embla-carousel-autoplay'
import ClassNames from 'embla-carousel-class-names'
import Fade from 'embla-carousel-fade'
import useEmblaCarousel from 'embla-carousel-react'
import {
  memo,
  useCallback,
  useEffect,
  useEffectEvent,
  useRef,
  useState
} from 'react'
import { BaseProps } from '../types'
import { useAutoplay } from './EmblaCarouselAutoplay'
import { useAutoplayProgress } from './EmblaCarouselAutoplayProgress'
import SliderArrows, { type SliderArrowsProps } from './SliderArrows'

export interface SliderProps extends BaseProps {
  children?: React.ReactNode
  showArrows?: boolean
  showProgressBar?: boolean
  slidesPerView?: number | 'auto'
  slidesPerViewMd?: number | 'auto'
  spaceBetween?: string
  header?: React.ReactNode
  showCounter?: boolean
  isContainer?: boolean
  sliderClass?: any
  sliderOptions?: EmblaOptionsType
  sliderIsFade?: boolean
  sliderAutoplay?: boolean
  counterPosition?: 'bottom-right' | 'top'
  arrowPosition?: SliderArrowsProps['arrowPosition']
}

const Slider = ({
  children,
  className,
  showArrows = true,
  showProgressBar = false,
  showCounter = false,
  counterPosition = 'bottom-right',
  slidesPerView = 4,
  slidesPerViewMd,
  spaceBetween = '1rem',
  isContainer = true,
  sliderOptions,
  sliderClass,
  sliderIsFade = false,
  sliderAutoplay = false,
  arrowPosition = 'top-right',
  ...props
}: SliderProps) => {
  const progressNode = useRef<HTMLDivElement>(null)

  // Initialize plugins
  const sliderPlugins = [ClassNames()]
  if (sliderIsFade) {
    sliderPlugins.push(Fade())
  }
  if (sliderAutoplay) {
    sliderPlugins.push(Autoplay({ delay: 4000 }))
  }

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      inViewThreshold: 0.5,
      align: 'start',
      ...sliderOptions
    },
    sliderPlugins
  )

  const ContainerComp = isContainer === true ? Container : Div

  //states
  const [scrollProgress, setScrollProgress] = useState(0)
  const [currentSlide, setCurrentSlide] = useState(1)
  const [totalSlides, setTotalSlides] = useState(0)
  const [disabledNext, setDisabledNext] = useState<boolean>(false)
  const [disabledPrev, setDisabledPrev] = useState<boolean>(true)
  const [navigationHidden, setNavigationHidden] = useState<boolean>(false)
  const [canScroll, setCanScroll] = useState<boolean>(true)

  const { autoplayIsPlaying, toggleAutoplay } = useAutoplay(emblaApi)

  const { showAutoplayProgress } = useAutoplayProgress(emblaApi, progressNode)

  const isDesktop = useMedia(MEDIA.desktop, true)

  const checkDisabled = useEffectEvent(() => {
    if (!emblaApi) {
      return
    }
    setDisabledPrev(!emblaApi.canScrollPrev())
    setDisabledNext(!emblaApi.canScrollNext())
  })

  const onScroll = useEffectEvent((emblaApi: EmblaCarouselType) => {
    const progress = Math.max(0, Math.min(1, emblaApi.scrollProgress()))
    setScrollProgress(progress * 75)
  })

  const onSlideChanged = useEffectEvent((emblaApi: EmblaCarouselType) => {
    setTotalSlides(emblaApi.slideNodes().length)
    setCurrentSlide(emblaApi.selectedScrollSnap() + 1)
  })

  const checkNav = useEffectEvent(() => {
    if (emblaApi) {
      if (isDesktop && emblaApi.canScrollNext() === false) {
        setNavigationHidden(true)
      } else {
        setNavigationHidden(false)
      }

      setCanScroll(emblaApi.canScrollPrev() || emblaApi.canScrollNext())
    }
  })

  const scrollPrev = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollPrev()
    }
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollNext()
    }
  }, [emblaApi])

  useEffect(() => {
    if (emblaApi) {
      // If there is only one slide, don't add the is-next class
      if (emblaApi.slideNodes().length <= 1) {
        return
      }

      const nextSlideIndex =
        (currentSlide === totalSlides ? 1 : currentSlide + 1) - 1

      emblaApi.slideNodes().forEach((slide, index) => {
        if (index === nextSlideIndex) {
          slide.classList.add('is-next')
        } else {
          slide.classList.remove('is-next')
        }
      })
    }
  }, [currentSlide, totalSlides, emblaApi])

  useEffect(() => {
    if (emblaApi) {
      emblaApi
        .on('init', checkNav)
        .on('resize', checkNav)
        .on('select', checkDisabled)
        .on('init', checkDisabled)

      onScroll(emblaApi)
      emblaApi
        .on('reInit', () => onScroll(emblaApi))
        .on('scroll', () => onScroll(emblaApi))
        .on('slideFocus', () => onScroll(emblaApi))

      onSlideChanged(emblaApi)
      emblaApi.on('select', () => onSlideChanged(emblaApi))
    }
  }, [emblaApi])

  // Calculate the value for the slides per view on MD
  const slidesPerViewMdValue = slidesPerViewMd
    ? slidesPerViewMd === 'auto'
      ? 'auto'
      : `${100 / slidesPerViewMd}%`
    : slidesPerView === 'auto'
      ? 'auto'
      : `${slidesPerView > 1 ? (100 / slidesPerView) * 2 : 100}%`

  return (
    <div className="-mt-14 overflow-hidden">
      <Base
        {...props}
        className={cn(className, 'relative mt-14')}
        anim="fade-in"
      >
        {showArrows === true &&
          arrowPosition === 'top-right' &&
          canScroll === true && (
            <ContainerComp className="relative">
              <SliderArrows
                scrollPrev={scrollPrev}
                scrollNext={scrollNext}
                disabledPrev={disabledPrev}
                disabledNext={disabledNext}
                navigationHidden={navigationHidden}
                className={'mb-lg right-padding-md absolute bottom-full'}
              />
            </ContainerComp>
          )}
        <ContainerComp className={cn('md:pb-md relative overflow-visible')}>
          <div
            className={cn('embla')}
            ref={emblaRef}
            style={{
              '--slides-per-view':
                slidesPerView === 'auto' ? 'auto' : `${100 / slidesPerView}%`,
              '--slides-per-view-md': slidesPerViewMdValue,
              '--slide-spacing': spaceBetween
            }}
          >
            <div
              className={cn(
                '-ml-(--slide-spacing) grid auto-cols-[100%] grid-flow-col md:auto-cols-(--slides-per-view-md) lg:auto-cols-(--slides-per-view)',
                sliderClass
              )}
            >
              {children}
            </div>
          </div>

          {showProgressBar === true &&
            !navigationHidden &&
            canScroll === true && (
              <div className="mt-padding-md relative mx-auto grid h-1 w-full max-w-56 overflow-hidden bg-neutral-200">
                <div
                  className="absolute left-0 h-full w-1/4 bg-neutral-600"
                  style={{
                    left: `${scrollProgress}%`
                  }}
                />
              </div>
            )}
          {showCounter && canScroll === true && (
            <div
              className={cn(
                'textstyle-lg-regular mt-md text-right md:absolute md:text-left',
                counterPosition === 'bottom-right'
                  ? 'right-md bottom-md'
                  : 'top-md right-md'
              )}
            >
              <span>{('0' + currentSlide).slice(-2)}</span>{' '}
              <span className="text-neutral-300">
                / {('0' + totalSlides).slice(-2)}
              </span>
            </div>
          )}

          <div className="bottom-md gap-md p-sm absolute right-0 z-20 flex w-full max-w-48 items-end justify-end">
            {sliderAutoplay === true && canScroll === true && (
              <>
                <div
                  className={cn(
                    `embla__progress relative h-1 w-full origin-right overflow-hidden rounded-full bg-neutral-100 transition-all duration-300`,
                    showAutoplayProgress ? '' : 'scale-x-0 opacity-0'
                  )}
                >
                  <div
                    className="embla__progress__bar animation-autoplay-progress bg-accent-100 absolute -left-full h-full w-full"
                    ref={progressNode}
                  />
                </div>

                {sliderAutoplay && (
                  <ButtonIcon
                    className="embla__play"
                    size="m"
                    onClick={toggleAutoplay}
                    icon={
                      autoplayIsPlaying
                        ? 'material-symbols:pause'
                        : 'material-symbols:play-arrow'
                    }
                    type="button"
                  />
                )}
              </>
            )}
            {showArrows === true &&
              arrowPosition === 'bottom-right' &&
              canScroll === true && (
                <SliderArrows
                  scrollPrev={scrollPrev}
                  scrollNext={scrollNext}
                  disabledPrev={disabledPrev}
                  disabledNext={disabledNext}
                  navigationHidden={navigationHidden}
                  className={''}
                />
              )}
          </div>
        </ContainerComp>
      </Base>
    </div>
  )
}

Slider.displayName = 'Slider'

export default memo(Slider)
